import Bugsnag from "@bugsnag/js";

/** Longest error text we are willing to render to a user. */
const RENDERABLE_TEXT_MAX_LENGTH = 300;

/**
 * True for error text that must never be rendered: gateway/proxy error pages
 * (HTML), unparsed JSON payloads, and anything long enough to blanket the UI
 * behind the toast that carries it.
 */
export function isUnrenderableErrorText(value: unknown): value is string {
  if (typeof value !== "string") return false;

  const trimmed = value.trim();

  return (
    trimmed.startsWith("<") ||
    trimmed.startsWith("{") ||
    trimmed.length > RENDERABLE_TEXT_MAX_LENGTH
  );
}

/**
 * Collapses an unrenderable body to a short message, sending the full body to
 * the console and to Bugsnag so the original is still diagnosable.
 *
 * `Bugsnag.notify` is inert when Bugsnag was never started (tests, local), so
 * the console log is the guaranteed half of the pair.
 */
export function reportUnrenderableErrorText(
  value: string,
  status?: number
): string {
  console.error("Unrenderable error response body", { status, body: value });

  Bugsnag.notify(
    new Error(
      status
        ? `Unrenderable error response body (status ${status})`
        : "Unrenderable error response body"
    ),
    (event) => {
      event.severity = "warning";
      event.addMetadata("response", { status, body: value });
    }
  );

  return status
    ? `Something went wrong (error ${status}). Please try again or contact support.`
    : "An unexpected error occurred. Please try again.";
}

/** Passes text through unless it is unrenderable, in which case it collapses. */
export function toRenderableErrorText(value: string, status?: number): string {
  return isUnrenderableErrorText(value)
    ? reportUnrenderableErrorText(value, status)
    : value;
}

/**
 * Safely extracts a human-readable error message from unknown error types.
 *
 * Handles:
 * - Axios errors with `response.data.error` or `response.data.message`
 * - Axios errors with non-JSON response bodies (HTML from gateway errors, etc.)
 * - Standard Error objects
 * - Plain string errors (e.g. re-thrown from api/post.ts)
 * - Fallback for anything else (undefined, null, etc.)
 */
export function getErrorMessage(error: unknown): string {
  // Already a usable string (e.g. re-thrown from api/post.ts catch blocks)
  if (typeof error === "string" && error) {
    return toRenderableErrorText(error);
  }

  if (error && typeof error === "object") {
    // Axios-style error with response.data
    if ("response" in error) {
      const response = (
        error as { response?: { data?: unknown; status?: number } }
      ).response;
      const data = response?.data;

      if (typeof data === "object" && data !== null) {
        const obj = data as Record<string, unknown>;
        if (typeof obj.error === "string" && obj.error)
          return toRenderableErrorText(obj.error, response?.status);
        if (typeof obj.message === "string" && obj.message)
          return toRenderableErrorText(obj.message, response?.status);
      }

      // Gateway / proxy errors that return plain HTML or text
      if (typeof data === "string" && data) {
        return toRenderableErrorText(data, response?.status);
      }
    }

    // Standard Error instance — its message may itself be a raw body, since
    // `extractApiError` deliberately keeps the original for Bugsnag.
    if (error instanceof Error && error.message) {
      return toRenderableErrorText(error.message);
    }
  }

  return "An unexpected error occurred. Please try again.";
}

/**
 * The text of one validator entry, whichever key it happens to use.
 *
 * Exported so `handleError` reads the same keys. It used to check `message`
 * only, so an express-validator entry, which carries `msg`, toasted
 * undefined and the reason was lost.
 */
export function readValidatorEntry(entry: unknown): string | null {
  if (typeof entry === "string") return entry || null;
  if (!entry || typeof entry !== "object") return null;
  const { message, msg } = entry as { message?: unknown; msg?: unknown };
  if (typeof message === "string" && message) return message;
  // express-validator uses `msg`.
  if (typeof msg === "string" && msg) return msg;
  return null;
}

/**
 * Whether a body is express-validator's `mapped()` output: a map keyed by field
 * name whose every value is an entry object carrying the message.
 *
 * Deliberately strict. `readErrorsField` would happily join the string values
 * of ANY object, so pointing it at a whole response body would turn something
 * like `{ status: "failed", code: "RATE_LIMIT" }` into the prose "failed
 * RATE_LIMIT" and show it to a user. Requiring every value to be an entry
 * object with a message keeps that body on the JSON path, where the render
 * boundary collapses it to a generic sentence and reports the real thing.
 */
export function isValidatorFieldMap(body: unknown): boolean {
  if (!body || typeof body !== "object" || Array.isArray(body)) return false;
  const values = Object.values(body as Record<string, unknown>);
  if (values.length === 0) return false;
  return values.every(isValidatorEntry);
}

/**
 * An express-validator entry, as opposed to any object that happens to carry a
 * message. The distinction matters: `{ error: { message: "Error validating
 * access token: ..." } }` is a single-key object whose value has a message, so
 * a looser test accepts it and puts Graph's internal prose in a toast, skipping
 * the unrenderable-body path that reports such bodies to Bugsnag. Requiring
 * express-validator's own keys (`msg`, or a message alongside `param`/`path`)
 * separates the two.
 */
function isValidatorEntry(entry: unknown): boolean {
  if (!entry || typeof entry !== "object" || Array.isArray(entry)) return false;
  const { msg, message, param, path } = entry as Record<string, unknown>;
  if (typeof msg === "string" && msg) return true;
  return (
    typeof message === "string" &&
    !!message &&
    (typeof param === "string" || typeof path === "string")
  );
}

/**
 * Pulls readable prose out of a validator payload. Three shapes are in use: a
 * bare string, an array of strings or objects, and a map keyed by field name
 * (what express-validator's `mapped()` produces).
 */
function readErrorsField(errors: unknown): string | null {
  if (typeof errors === "string") return errors || null;
  if (!errors || typeof errors !== "object") return null;

  // An object is only walked when it really is a field map. Walking any object
  // would join the string values of, say, `{ status: "failed", code: "X" }`
  // into the prose "failed X" and show it to a user. That applies here too,
  // not only to the whole body: `{ errors: { status: "failed" } }` is the same
  // hazard one key deeper.
  if (!Array.isArray(errors) && !isValidatorFieldMap(errors)) return null;

  const entries = Array.isArray(errors)
    ? errors
    : Object.values(errors as Record<string, unknown>);

  const messages = entries
    .map(readValidatorEntry)
    .filter((entry): entry is string => !!entry);

  return messages.length > 0 ? messages.join(" ") : null;
}

/**
 * Extracts the API error for re-throwing from api/ catch blocks.
 *
 * Always returns a proper Error object so that:
 * - Bugsnag captures the actual message (strings are lost as "non-error")
 * - getErrorMessage() can extract it via instanceof Error
 * - Unhandled rejections are debuggable
 */
export function extractApiError(error: unknown): Error {
  if (error && typeof error === "object" && "isAxiosError" in error) {
    const axiosErr = error as {
      response?: {
        data?: {
          error?: string;
          message?: string;
          errors?: unknown;
        };
      };
    };
    const data = axiosErr.response?.data;

    if (typeof data === "object" && data !== null) {
      if (typeof data.error === "string" && data.error)
        return new Error(data.error);
      if (typeof data.message === "string" && data.message)
        return new Error(data.message);
      // Validator responses carry the reason under `errors`. Reach it before
      // falling back to JSON: a stringified body starts with `{`, which the
      // render boundary treats as unrenderable, so the reason would be lost.
      const fromErrors = readErrorsField(data.errors);
      if (fromErrors) return new Error(fromErrors);
      // Some validators return the field map AS the body, with no wrapper key
      // at all (express-validator's `mapped()`), so try the body itself.
      // `isValidatorFieldMap` decides on SHAPE, not on how many fields there
      // are: a one-field validation error is real and must be read. What it
      // rejects is a wrapper whose value merely carries a message, such as
      // `{ error: { message: "Error validating access token: ..." } }`, which
      // would otherwise put internal detail in a toast and skip the
      // unrenderable-body path that reports such bodies to Bugsnag.
      if (isValidatorFieldMap(data)) {
        const fromBody = readErrorsField(data);
        if (fromBody) return new Error(fromBody);
      }
      return new Error(JSON.stringify(data));
    }

    if (typeof data === "string" && data) {
      return new Error(data);
    }
  }

  if (error instanceof Error) {
    return error;
  }

  return new Error(
    typeof error === "string" && error ? error : "An unexpected error occurred"
  );
}
